
import React from 'react';
import {motion} from 'framer-motion';
import HeroSection from './HeroSection';
import TilesDemo from './TilesDemo';
import CallToAction from './CallToAction';
import {FileText, Lightbulb, TrendingUp, Users} from 'lucide-react';
import './LandingPage.css';
import TilesBridgesAnimation from "./TilesBridgesAnimation";
import WhoComponent from "./WhoComponent";
import UseCasesSection from "./UseCasesSection";
import PhilosophyComponent from "./PhilosophyComponent";

const LandingPage: React.FC = () => {
  return (
      <div className="landing-page">
        {/* Enhanced Glass Background System */}
        <div className="glass-background">
          <div className="glass-layer glass-layer-1">
            <div className="glass-shimmer"></div>
          </div>
          <div className="glass-layer glass-layer-2">
            <div className="glass-shimmer"></div>
          </div>
          <div className="glass-layer glass-layer-3">
            <div className="glass-shimmer"></div>
          </div>
        </div>

        {/* Subtle Rice Paper Particles */}
        <div className="glass-particles">
          {[...Array(8)].map((_, i) => (
              <div
                  key={i}
                  className="glass-particle"
                  style={{
                    width: `${Math.random() * 20 + 10}px`,
                    height: `${Math.random() * 20 + 10}px`,
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 20}s`,
                    animationDuration: `${Math.random() * 10 + 15}s`
                  }}
              >
                <div className="particle-glow"></div>
              </div>
          ))}
        </div>

        <div className="content-wrapper">
          {/* Hero Section */}
          <HeroSection/>

          {/* Elegant Tagline Bridge Component */}
          <motion.div
              className="tagline-bridge"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 1.8,
                ease: [0.25, 0.1, 0.25, 1]
              }}
          >
            <div className="tagline-glass-container">
              <div className="tagline-backdrop"></div>
              <div className="tagline-glow"></div>

              <div className="tagline-content">
                <motion.h3
                    className="tagline-text"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 2.2 }}
                >
                  You deserve <span className="tagline-emphasis">clarity</span>.
                </motion.h3>

                <motion.p
                    className="tagline-description"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 2.5 }}
                >
                  Augments is your <span className="tagline-highlight">Mental OS</span> connecting documents, research, and decisions so ideas flow, teams align, and noise disappears.
                </motion.p>

                <motion.div
                    className="tagline-connector"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.2, delay: 3, ease: [0.25, 0.1, 0.25, 1] }}
                ></motion.div>
              </div>
            </div>
          </motion.div>

          {/*Introduction Demo*/}
          <TilesBridgesAnimation/>

          {/* Elevated Introduction Section */}
          <motion.div
              className="reading-container introduction-section"
              initial={{opacity: 0, y: 50}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 2.5}}
              viewport={{once: true}}
          >
            <div className="glass-tile-container">
              <div className="glass-tile-backdrop"></div>
              <div className="glass-tile-content">
                <div className="section-header">
                  <h2 className="primary-heading">Why augments?</h2>
                  <div className="heading-accent"></div>
                </div>

                <PhilosophyComponent />

                <motion.div
                    className="methodology-highlight"
                    initial={{opacity: 0, x: 30}}
                    animate={{opacity: 1, x: 0}}
                    transition={{duration: 0.8, delay: 3}}
                >
                  <div className="glass-accent-tile">
                    <div className="highlight-badge">Core Innovation</div>
                    <p className="methodology-text">
                      <strong>Tiles & Bridges</strong> — Like neurons forming ideas, Tiles capture your
                      thoughts. Bridges connect them into meaning, helping you discover insights and
                      relationships naturally.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Elegant Who Component */}
          <WhoComponent />



          {/* Tiles Demo Section */}
          {/*<TilesDemo/>*/}

          {/* Elegant Separator */}
          <div className="professional-separator">
            <div className="separator-content">
              <div className="separator-line"></div>
              <div className="separator-dot"></div>
              <div className="separator-line"></div>
            </div>
          </div>

          {/* Privacy Excellence Section */}
          <motion.div
              className="reading-container privacy-section"
              initial={{opacity: 0, y: 50}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 1}}
              viewport={{once: true, margin: "-100px"}}
          >
            <div className="glass-tile-container">
              <div className="glass-tile-backdrop"></div>
              <div className="glass-tile-content">
                <div className="section-header">
                  <h2 className="primary-heading">Privacy as Foundation</h2>
                  <div className="heading-accent"></div>
                </div>

                <div className="two-column-layout">
                  <div className="column-primary">
                    <div className="glass-inner-tile">
                      <p className="lead-text">
                        Because your thoughts are sacred, we built Augments on a foundation of absolute
                        trust and transparency.
                      </p>
                      <p className="supporting-text">
                        In a world where data is exploited, we choose to protect. Your knowledge is yours
                        alone — never harvested, never sold, never compromised.
                      </p>
                      <p className="supporting-text">
                        Our commitment to GDPR compliance and ethical stewardship means Augments empowers
                        you without ever invading your intellectual sovereignty.
                      </p>
                    </div>
                  </div>

                  <div className="column-secondary">
                    <div className="commitment-box glass-feature-tile">
                      <div className="commitment-header">
                        <div className="commitment-icon">
                          <svg viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.5"
                                  strokeLinecap="round"/>
                          </svg>
                        </div>
                        <h4>Our Guarantee</h4>
                      </div>
                      <ul className="commitment-list">
                        <li>
                          <div className="list-icon">
                            <svg viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6" fill="currentColor"/>
                            </svg>
                          </div>
                          Zero data mining or algorithmic manipulation
                        </li>
                        <li>
                          <div className="list-icon">
                            <svg viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6" fill="currentColor"/>
                            </svg>
                          </div>
                          Complete intellectual property ownership
                        </li>
                        <li>
                          <div className="list-icon">
                            <svg viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6" fill="currentColor"/>
                            </svg>
                          </div>
                          Transparent, auditable privacy practices
                        </li>
                        <li>
                          <div className="list-icon">
                            <svg viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6" fill="currentColor"/>
                            </svg>
                          </div>
                          European data sovereignty standards
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Professional Separator */}
          <div className="professional-separator">
            <div className="separator-content">
              <div className="separator-line"></div>
              <div className="separator-dot"></div>
              <div className="separator-line"></div>
            </div>
          </div>

          {/* Capabilities Showcase */}
          <motion.div
              className="reading-container capabilities-section"
              initial={{opacity: 0, y: 50}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 1}}
              viewport={{once: true, margin: "-100px"}}
          >
            <div className="glass-tile-container">
              <div className="glass-tile-backdrop"></div>
              <div className="glass-tile-content">
                <div className="section-header">
                  <h2 className="primary-heading">Engineered for You</h2>
                  <div className="heading-accent"></div>
                </div>

                <div className="capabilities-grid">
                  <div className="capability-card glass-capability-tile">
                    <div className="capability-icon">
                      <svg viewBox="0 0 48 48" fill="none">
                        <rect x="8" y="8" width="32" height="32" rx="6" stroke="currentColor"
                              strokeWidth="2"/>
                        <rect x="16" y="16" width="6" height="6" rx="1" fill="currentColor"/>
                        <rect x="26" y="16" width="6" height="6" rx="1" fill="currentColor"/>
                        <rect x="16" y="26" width="6" height="6" rx="1" fill="currentColor"/>
                        <rect x="26" y="26" width="6" height="6" rx="1" fill="currentColor"/>
                        <circle cx="24" cy="24" r="2" stroke="currentColor" strokeWidth="1"/>
                      </svg>
                    </div>
                    <h4>Cognitive Architecture</h4>
                    <p>Our Tiles & Bridges system transforms complex information into interconnected
                      knowledge networks that mirror natural thought patterns.</p>
                  </div>

                  <div className="capability-card glass-capability-tile">
                    <div className="capability-icon">
                      <svg viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="16" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="24" cy="24" r="8" stroke="currentColor" strokeWidth="1.5"/>
                        <circle cx="24" cy="24" r="3" fill="currentColor"/>
                        <path d="M24 8v4M24 36v4M8 24h4M36 24h4" stroke="currentColor" strokeWidth="1.5"
                              strokeLinecap="round"/>
                      </svg>
                    </div>
                    <h4>Intuitive Elegance</h4>
                    <p>A design so thoughtful, it disappears. Leaving only your ideas in focus.</p>
                  </div>

                  <div className="capability-card glass-capability-tile">
                    <div className="capability-icon">
                      <svg viewBox="0 0 48 48" fill="none">
                        <path
                            d="M24 8c8.837 0 16 7.163 16 16s-7.163 16-16 16-16-7.163-16-16S15.163 8 24 8z"
                            stroke="currentColor" strokeWidth="2"/>
                        <path d="M24 16c4.418 0 8 3.582 8 8s-3.582 8-8 8-8-3.582-8-8 3.582-8 8-8z"
                              stroke="currentColor" strokeWidth="1.5"/>
                        <circle cx="24" cy="24" r="2" fill="currentColor"/>
                        <path d="M16 16l4 4M32 16l-4 4M16 32l4-4M32 32l-4-4" stroke="currentColor"
                              strokeWidth="1" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <h4>Augmented Cognition</h4>
                    <p> Tools that elevate your thinking without distraction, seamlessly enhancing your
                      flow.</p>
                  </div>

                  <div className="capability-card glass-capability-tile">
                    <div className="capability-icon">
                      <svg viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="16" stroke="currentColor" strokeWidth="2"/>
                        {/* Star circle */}
                        {[...Array(12)].map((_, i) => {
                          const angle = (i * 30) * Math.PI / 180;
                          const x = 24 + 10 * Math.sin(angle);
                          const y = 24 - 10 * Math.cos(angle);
                          return (
                              <path
                                  key={i}
                                  d="M 0,-3 L 0.9,-0.9 L 3,0 L 0.9,0.9 L 0,3 L -0.9,0.9 L -3,0 L -0.9,-0.9 Z"
                                  fill="currentColor"
                                  transform={`translate(${x}, ${y}) scale(0.7)`}
                              />
                          );
                        })}
                      </svg>
                    </div>
                    <h4>European Heritage</h4>
                    <p>Crafted with deep respect for culture, freedom, and sustainable innovation.
                      Because how we work shapes who we are.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Professional Separator */}
          <div className="professional-separator">
            <div className="separator-content">
              <div className="separator-line"></div>
              <div className="separator-dot"></div>
              <div className="separator-line"></div>
            </div>
          </div>

          {/* Exclusive Invitation Section */}
          <motion.div
              className="reading-container invitation-section"
              initial={{opacity: 0, y: 50}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 1}}
              viewport={{once: true, margin: "-100px"}}
          >
            <div className="glass-tile-container">
              <div className="glass-tile-backdrop"></div>
              <div className="glass-tile-content">
                <div className="section-header">
                  <h2 className="primary-heading">The Future of Knowledge Work</h2>
                  <div className="heading-accent"></div>
                </div>

                <div className="invitation-content">
                  <div className="glass-inner-tile">
                    <p className="lead-text">
                      This is for those who <span className="text-bold">never settled</span> for <span className="text-italic">noisy feeds</span>, <span className="text-bold">rigid tools</span>, or <span className="text-italic">data trade-offs</span>.
                      For those who value <span className="text-bold">privacy</span> as deeply as <span className="text-italic">clarity</span>.
                      For those who still believe technology should <span className="text-bold-italic">amplify</span>, not replace, <span className="text-bold">human thought</span>.
                    </p>

                  </div>

                  <div className="exclusivity-banner glass-premium-tile">
                    <div className="banner-icon">
                      <svg viewBox="0 0 32 32" fill="none">
                        <rect x="8" y="14" width="16" height="12" rx="2" stroke="currentColor"
                              strokeWidth="2"/>
                        <path d="M12 14V10a4 4 0 0 1 8 0v4" stroke="currentColor" strokeWidth="2"
                              strokeLinecap="round"/>
                        <circle cx="16" cy="20" r="2" fill="currentColor"/>
                      </svg>
                    </div>
                    <div className="banner-content">
                      <h4>Join us as a Founding Member </h4>
                      <p>And help us shape the future where technology serves the mind, not the other
                        way around.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

           {/*Call to Action*/}
          <CallToAction/>

           {/*Refined Footer*/}
          <motion.footer
              className="footer-section"
              initial={{opacity: 0}}
              whileInView={{opacity: 1}}
              transition={{duration: 1}}
              viewport={{once: true}}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
              }}
          >

          </motion.footer>
        </div>
      </div>
  );
};

export default LandingPage;